import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Calendar,
  Badge,
  Button,
  Input,
  Select,
  Avatar,
  DatePicker,
  ConfigProvider,
  Spin,
  message,
  List,
  Modal,
  Tag
} from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
  PlusOutlined,
  MessageOutlined,
  MoreOutlined,
  CloseOutlined
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { getData } from '../../../actions/apiCall';
import axios from 'axios';
import { getAccess } from '../../../actions/auth';
import InfiniteScrollList from '../../../components/Forms/ListView/InfiniteScrollList';

// Import both Modals
import ScheduleTimelineModal, { DailyTimeline, ColorKey } from './ScheduleTimelineModal';
import InterviewScheduleModal, { TimelineEvent } from './InterviewScheduleModal';
import FeedbackDrawer from './RecentFeedback';
import AddInterviewModal from './AddInterviewModal';

const { RangePicker } = DatePicker;

const CustomCalendar: React.FC = () => {
  const dispatch = useDispatch();
  const [currentValue, setCurrentValue] = useState<Dayjs>(dayjs());
  const [view, setView] = useState<'month' | 'week' | 'day'>('month');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [scheduleData, setScheduleData] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [isAddInterviewModalVisible, setIsAddInterviewModalVisible] = useState(false);
  const [selectedDateForInterview, setSelectedDateForInterview] = useState<Dayjs | undefined>(undefined);
  const [selectedHour, setSelectedHour] = useState<number | null>(null);
  const [editInterviewData, setEditInterviewData] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [benchFilter, setBenchFilter] = useState<string>('all'); // 'vendor', 'own', 'all'
  const [reloadEmployees, setReloadEmployees] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleOpen = () => setIsDrawerOpen(true);
  const handleClose = () => setIsDrawerOpen(false);

  const handleSubmit = () => {
    console.log("Form Submitted");
    // Add your save logic here
    setIsDrawerOpen(false);
  };

  // Modal 1 State (Timeline List)
  const [isTimelineVisible, setIsTimelineVisible] = useState(false);
  const [selectedTimelineData, setSelectedTimelineData] = useState<DailyTimeline[]>([]);
  const [timelineTitle, setTimelineTitle] = useState<string>('');

  // Modal 2 State (Specific Interview Detail)
  const [isDetailVisible, setIsDetailVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | undefined>(undefined);

  // Employee Interviews List Modal State
  const [isEmployeeInterviewsVisible, setIsEmployeeInterviewsVisible] = useState(false);
  const [employeeInterviews, setEmployeeInterviews] = useState<any[]>([]);
  const [selectedEmployeeForInterviews, setSelectedEmployeeForInterviews] = useState<any>(null);
  const [loadingInterviews, setLoadingInterviews] = useState(false);

  // Calculate total interview count from scheduleData
  const totalInterviewCount = useMemo(() => {
    return Object.values(scheduleData).reduce((total, events) => total + (events?.length || 0), 0);
  }, [scheduleData]);

  // Fetch interview counts by date range
  const fetchInterviewCounts = async (startDate: string, endDate: string, employeeId?: string | null) => {
    setLoading(true);
    try {
      axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
      const employeeParam = employeeId ? `&employeeId=${employeeId}` : '';
      const response = await dispatch(
        getData(`/calendar/interviews/count-by-date?startDate=${startDate}&endDate=${endDate}${employeeParam}`)
      );

      if (response?.code === 200 && response?.data) {
        const dataMap: Record<string, any[]> = {};

        response.data.forEach((item: any) => {
          const dateStr = item.date;
          const events = item.interviews.map((interview: any, idx: number) => {
            // Parse time string (format: "HH:mm AM/PM" or "H:mm AM/PM")
            const timeStr = interview.time || '';
            const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
            let hour24 = 0;
            let minute = 0;

            if (timeMatch) {
              hour24 = parseInt(timeMatch[1]);
              minute = parseInt(timeMatch[2]);
              const period = timeMatch[3].toUpperCase();
              if (period === 'PM' && hour24 !== 12) hour24 += 12;
              if (period === 'AM' && hour24 === 12) hour24 = 0;
            } else {
              // Fallback: try to parse as "HH:mm" format
              const [hour, min] = timeStr.split(':') || ['0', '0'];
              hour24 = parseInt(hour) || 0;
              minute = parseInt(min) || 0;
            }

            // Build display text with account name, developer name, and level
            const accountName = interview.accountName || interview.dealName || '';
            const developerName = interview.developerName || '';
            const level = interview.level || '';
            const displayText = [accountName, developerName, level].filter(Boolean).join(' • ') || 'Interview Schedule';

            return {
              id: `${dateStr}-${idx}`,
              type: 'pink',
              text: displayText,
              hour: hour24,
              minute: minute,
              status: 'upcoming',
              interviewData: interview
            };
          });

          if (events.length > 0) {
            dataMap[dateStr] = events;
          }
        });

        setScheduleData(dataMap);
      }
    } catch (error) {
      console.error('Error fetching interview counts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch interview details for a specific date
  const fetchInterviewDetails = async (date: string) => {
    try {
      axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
      const response = await dispatch(
        getData(`/calendar/interviews/details?date=${date}${selectedEmployeeId ? `&employeeId=${selectedEmployeeId}` : ''}`)
      );

      if (response?.code === 200 && response?.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching interview details:', error);
      return [];
    }
  };

  // Fetch all interviews for an employee
  const fetchEmployeeInterviews = async (employeeId: string) => {
    setLoadingInterviews(true);
    try {
      axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
      const response = await dispatch(
        getData(`/calendar/interviews/developer/${employeeId}`)
      );

      if (response?.code === 200 && response?.data) {
        setEmployeeInterviews(response.data.interviews || []);
        return response.data.interviews || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching employee interviews:', error);
      message.error('Failed to fetch interviews');
      return [];
    } finally {
      setLoadingInterviews(false);
    }
  };

  // Handle click on interview count badge - show employee interviews in timeline
  const handleInterviewCountClick = async (e: React.MouseEvent, employee: any) => {
    e.stopPropagation(); // Prevent triggering the employee selection

    const startOfMonth = currentValue.startOf('month').format('YYYY-MM-DD');
    const endOfMonth = currentValue.endOf('month').format('YYYY-MM-DD');

    try {
      setLoading(true);
      axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
      const employeeParam = employee._id ? `&employeeId=${employee._id}` : '';
      const response = await dispatch(
        getData(`/calendar/interviews/count-by-date?startDate=${startOfMonth}&endDate=${endOfMonth}${employeeParam}`)
      );

      if (response?.code === 200 && response?.data) {
        // Transform the response to timeline format
        const timelineData: DailyTimeline[] = response.data.map((item: any) => {
          const dateObj = dayjs(item.date);
          return {
            date: dateObj.format('ddd, DD MMM YYYY'), // Format: "Mon, 09 Mar 2024"
            events: item.interviews.map((interview: any, idx: number) => {
              // Parse time to determine if interview is past
              const interviewTime = interview.time || '';
              const timeMatch = interviewTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
              let isPast = false;

              if (timeMatch && dateObj) {
                let hour = parseInt(timeMatch[1]);
                const period = timeMatch[3].toUpperCase();
                if (period === 'PM' && hour !== 12) hour += 12;
                if (period === 'AM' && hour === 12) hour = 0;

                const interviewDateTime = dateObj.hour(hour).minute(parseInt(timeMatch[2]));
                isPast = interviewDateTime.isBefore(dayjs());
              } else {
                isPast = dateObj.isBefore(dayjs(), 'day');
              }

              return {
                id: `${item.date}-${idx}`,
                time: interview.time || 'N/A',
                duration: `${interview?.duration} min` || '45 min',
                title: 'Interview Schedule',
                description: `Interview scheduled by ${interview.interviewerName || 'N/A'} for ${interview.job_description || 'N/A'}`,
                colorKey: (isPast ? 'purple' : 'orange') as ColorKey,
                status: (isPast ? 'completed' : 'active') as 'completed' | 'active' | 'upcoming'
              };
            })
          };
        });

        setSelectedTimelineData(timelineData);
        setTimelineTitle(employee.name ? `${employee.name}'s Interviews` : 'Scheduled Interviews');
        setIsTimelineVisible(true);
      }
    } catch (error) {
      console.error('Error fetching employee interviews for timeline:', error);
      message.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  // Handle click on total interview count badge - show all interviews in timeline
  const handleTotalInterviewCountClick = async () => {
    const startOfMonth = currentValue.startOf('month').format('YYYY-MM-DD');
    const endOfMonth = currentValue.endOf('month').format('YYYY-MM-DD');

    try {
      setLoading(true);
      axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
      const employeeParam = selectedEmployeeId ? `&employeeId=${selectedEmployeeId}` : '';
      const response = await dispatch(
        getData(`/calendar/interviews/count-by-date?startDate=${startOfMonth}&endDate=${endOfMonth}${employeeParam}`)
      );

      if (response?.code === 200 && response?.data) {
        // Transform the response to timeline format
        const timelineData: DailyTimeline[] = response.data.map((item: any) => {
          const dateObj = dayjs(item.date);
          return {
            date: dateObj.format('ddd, DD MMM YYYY'), // Format: "Mon, 09 Mar 2024"
            events: item.interviews.map((interview: any, idx: number) => {
              // Parse time to determine if interview is past
              const interviewTime = interview.time || '';
              const timeMatch = interviewTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
              let isPast = false;

              if (timeMatch && dateObj) {
                let hour = parseInt(timeMatch[1]);
                const period = timeMatch[3].toUpperCase();
                if (period === 'PM' && hour !== 12) hour += 12;
                if (period === 'AM' && hour === 12) hour = 0;

                const interviewDateTime = dateObj.hour(hour).minute(parseInt(timeMatch[2]));
                isPast = interviewDateTime.isBefore(dayjs());
              } else {
                isPast = dateObj.isBefore(dayjs(), 'day');
              }


              return {
                id: `${item.date}-${idx}`,
                time: interview.time || 'N/A',
                duration: `${interview?.duration} min` || '45 min',
                title: 'Interview Schedule',
                description: `Interview scheduled by ${interview.interviewerName || 'N/A'} for ${interview.job_description || 'N/A'}`,
                colorKey: (isPast ? 'purple' : 'orange') as ColorKey,
                status: (isPast ? 'completed' : 'active') as 'completed' | 'active' | 'upcoming'
              };
            })
          };
        });

        setSelectedTimelineData(timelineData);
        setTimelineTitle('Scheduled Interviews');
        setIsTimelineVisible(true);
      }
    } catch (error) {
      console.error('Error fetching interviews for timeline:', error);
      message.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };


  // Refresh interview data
  const refreshInterviewData = () => {
    const startOfMonth = currentValue.startOf('month').format('YYYY-MM-DD');
    const endOfMonth = currentValue.endOf('month').format('YYYY-MM-DD');
    fetchInterviewCounts(startOfMonth, endOfMonth, selectedEmployeeId);
  };

  // Fetch data when month changes
  useEffect(() => {
    refreshInterviewData();
  }, [currentValue, selectedEmployeeId]);

  // Debounce search query
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Trigger reload when filter or debounced search changes
  useEffect(() => {
    setReloadEmployees(true);
  }, [benchFilter, debouncedSearchQuery]);

  const hours = Array.from({ length: 24 }, (_, i) => i);

  const weekDays = useMemo(() => {
    const startOfWeek = currentValue.startOf('week');
    return Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day'));
  }, [currentValue]);

  // Handler to open Detail Modal
  const handleEventClick = async (e: React.MouseEvent, eventData: any, date: Dayjs) => {
    e.stopPropagation(); // Prevents triggering the cell click (Timeline Modal)

    const dateStr = date.format('YYYY-MM-DD');
    const details = await fetchInterviewDetails(dateStr);

    // Find the matching interview from details
    const interviewDetail = details.find((detail: any) => {
      const detailTime = detail.interview?.time?.from || '';
      const eventHour = eventData.hour;

      if (!detailTime) return false;

      const timeParts = detailTime.match(/(\d+):(\d+)\s*(AM|PM)/i);
      if (!timeParts) return false;

      let detailHour = parseInt(timeParts[1]);
      const period = timeParts[3].toUpperCase();

      if (period === 'PM' && detailHour !== 12) {
        detailHour += 12;
      } else if (period === 'AM' && detailHour === 12) {
        detailHour = 0;
      }

      return detailHour === eventHour;
    }) || details[0];

    if (interviewDetail) {
      // Ensure we have a valid interviewId from the API response
      const validInterviewId = interviewDetail.interviewId || interviewDetail._id || interviewDetail.interview?._id;

      if (!validInterviewId) {
        message.error('Interview ID not found in the response');
        console.error('Missing interviewId in interviewDetail:', interviewDetail);
        return;
      }

      // Format time with minutes
      const formatTime = (hour: number, minute: number = 0) => {
        const hour12 = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
        const period = hour >= 12 ? 'PM' : 'AM';
        return `${hour12}:${String(minute || 0).padStart(2, '0')} ${period}`;
      };

      const eventTime = interviewDetail.interview?.time?.from || formatTime(eventData.hour, eventData.minute || 0);

      const detailedEvent: TimelineEvent = {
        id: validInterviewId,
        interviewId: validInterviewId,
        time: eventTime,
        duration: interviewDetail.interview?.time?.duration || '1h',
        title: 'Interview Schedule',
        status: 'upcoming',
        candidate: {
          name: interviewDetail.developer?.name || 'N/A',
          email: interviewDetail.developer?.email || '',
          _id: interviewDetail.developer?._id
        },
        interviewer: {
          name: interviewDetail.interviewer?.name || 'N/A',
          email: interviewDetail.interviewer?.email || '',
          _id: interviewDetail.interviewer?._id
        },
        role: interviewDetail.developer?.role || interviewDetail.interview?.job_description || 'N/A',
        dateStr: date.format('DD MMMM YYYY'),
        meetingLink: interviewDetail.interview?.meetingLink || '',
        interviewLevel: interviewDetail.interview?.level,
        developerId: interviewDetail.developer?._id
      };

      setSelectedEvent(detailedEvent);
      setIsDetailVisible(true);
    }
  };

  // Handler for edit interview
  const handleEditInterview = async (event: TimelineEvent) => {
    if (!event.interviewId) {
      message.error('Interview ID is missing');
      return;
    }

    try {
      axios.defaults.headers.common['permissions'] = JSON.stringify(getAccess());
      const response = await dispatch(getData(`/contract_interview/${event.interviewId}`));

      if (response?.code === 200 && response?.data) {
        setEditInterviewData(response.data);
        setIsEditMode(true);
        setIsAddInterviewModalVisible(true);
      } else {
        message.error('Failed to load interview details');
      }
    } catch (error) {
      console.error('Error fetching interview details:', error);
      message.error('Failed to load interview details');
    }
  };

  // Handler for feedback added
  const handleFeedbackAdded = () => {
    refreshInterviewData();
  };

  // Handler to open Timeline Modal or Create Interview Modal
  const handleCellClick = async (date: Dayjs, event?: React.MouseEvent, hour?: number) => {
    // If Ctrl/Cmd key is pressed, open add interview modal instead
    if (event && (event.ctrlKey || event.metaKey)) {
      const dateWithTime = hour !== undefined
        ? date.hour(hour).minute(0).second(0)
        : date.hour(9).minute(0).second(0); // Default to 9 AM if no hour specified
      setSelectedDateForInterview(dateWithTime);
      setSelectedHour(hour !== undefined ? hour : null);
      setIsAddInterviewModalVisible(true);
      return;
    }

    const dateStr = date.format('YYYY-MM-DD');

    // Check if cell is empty based on scheduleData (no need to fetch if we can see it's empty)
    const hasEvents = hour !== undefined
      ? scheduleData[dateStr]?.some(e => e.hour === hour) || false
      : scheduleData[dateStr]?.length > 0 || false;

    // If cell appears empty, open create interview modal directly
    if (!hasEvents) {
      const dateWithTime = hour !== undefined
        ? date.hour(hour).minute(0).second(0)
        : date.hour(9).minute(0).second(0); // Default to 9 AM if no hour specified
      setSelectedDateForInterview(dateWithTime);
      setSelectedHour(hour !== undefined ? hour : null);
      setIsAddInterviewModalVisible(true);
      return;
    }

    // If cell has events, fetch details and show timeline modal
    const details = await fetchInterviewDetails(dateStr);
    const timelineEvents: DailyTimeline = {
      date: date.format('DD MMMM YYYY'),
      events: details.map((detail: any, idx: number) => ({
        id: detail.interviewId || `ev-${idx}`,
        time: detail.interview?.time?.from || 'N/A',
        duration: detail.interview?.time?.duration || '1h',
        title: 'Interview Schedule',
        description: `Interview scheduled by ${detail.interviewer?.name || 'N/A'} for ${detail.developer?.role || 'N/A'} role`,
        colorKey: 'orange',
        status: 'active'
      }))
    };

    setSelectedTimelineData([timelineEvents]);
    setTimelineTitle(date.format('DD MMMM YYYY'));
    setIsTimelineVisible(true);
  };

  const dateCellRender = (value: Dayjs) => {
    const dateStr = value.format('YYYY-MM-DD');
    const listData = scheduleData[dateStr] || [];
    const isEmpty = listData.length === 0;

    return (
      <div
        className={`flex flex-col gap-1 mt-1 h-full w-full ${isEmpty ? 'cursor-pointer hover:bg-slate-50' : ''}`}
        onClick={(e) => {
          // Only handle click if cell is empty or if clicking on empty area
          if (isEmpty || (e.target === e.currentTarget)) {
            handleCellClick(value, e);
          }
        }}
      >
        {listData.slice(0, 2).map((item, index) => (
          <div
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              handleEventClick(e, item, value);
            }}
            className={`px-1 rounded text-[9px] border-l-2 flex items-center h-5 truncate cursor-pointer hover:brightness-95 transition-all ${item.type === 'pink' ? 'bg-pink-50 border-pink-400 text-pink-700' : 'bg-purple-50 border-purple-400 text-purple-700'
              }`}>
            <span className="font-bold mr-1">
              {String(item.hour).padStart(2, '0')}:{String(item.minute || 0).padStart(2, '0')}
            </span> {item.text}
          </div>
        ))}
        {isEmpty && (
          <div className="text-[9px] text-slate-400 text-center mt-1 opacity-0 hover:opacity-100 transition-opacity">
            Click to add interview
          </div>
        )}
      </div>
    );
  };

  const TimeGridView = () => {
    const displayDays = view === 'day' ? [currentValue] : weekDays;

    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <div className="flex border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="w-16 border-r border-slate-100 flex items-end justify-center pb-2 text-[10px] text-slate-400 font-bold uppercase">Time</div>
          {displayDays.map((day) => (
            <div key={day.toString()} className="flex-1 text-center py-2 border-r border-slate-100 last:border-r-0">
              <div className={`text-xs font-bold ${day.isSame(dayjs(), 'day') ? 'text-blue-600' : 'text-slate-600'}`}>
                {day.format('DD')}
              </div>
              <div className="text-[10px] text-slate-400 uppercase font-medium">{day.format('dddd')}</div>
            </div>
          ))}
        </div>

        {hours.map((hour) => (
          <div key={hour} className="flex border-b border-slate-50 min-h-[80px]">
            <div className="w-16 border-r border-slate-100 text-center pt-2 text-xs text-slate-400 font-medium">
              {hour}:00
            </div>
            {displayDays.map((day) => {
              const dateStr = day.format('YYYY-MM-DD');
              // Filter events by hour (and optionally by minute for more precise matching)
              const events = scheduleData[dateStr]?.filter(e => e.hour === hour) || [];
              const isEmpty = events.length === 0;

              return (
                <div
                  key={day.toString()}
                  className={`flex-1 border-r border-slate-50 last:border-r-0 p-1 relative ${isEmpty ? 'hover:bg-slate-50/50 cursor-pointer' : ''}`}
                  onClick={(e) => {
                    // Only handle click if cell is empty or if clicking on empty area
                    if (isEmpty || (e.target === e.currentTarget)) {
                      handleCellClick(day, e, hour);
                    }
                  }}
                >
                  {events.map((event, idx) => (
                    <div
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(e, event, day);
                      }}
                      className={`mb-1 p-2 rounded-lg border-l-4 shadow-sm text-[10px] cursor-pointer hover:shadow-md transition-shadow ${event.type === 'pink' ? 'bg-pink-50 border-pink-400 text-pink-700' : 'bg-purple-50 border-purple-400 text-purple-700'
                        }`}>
                      <div className="font-bold truncate">{event.text || 'Interview Schedule'}</div>
                      {event.interviewData?.developerName && (
                        <div className="opacity-70 mt-1 truncate text-[9px]">
                          {event.interviewData.developerName}
                          {event.interviewData.accountName && ` • ${event.interviewData.accountName}`}
                          {event.interviewData.level && ` • ${event.interviewData.level}`}
                        </div>
                      )}
                    </div>
                  ))}
                  {isEmpty && (
                    <div className="text-[9px] text-slate-400 text-center mt-2 opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
                      Click to add
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#1677ff', borderRadius: 8 } }}>
      <div className="flex h-screen w-full bg-slate-50 p-2 gap-2 overflow-hidden font-sans">
        <aside className="w-72 flex flex-col gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
            <div className="flex justify-between items-center mb-4">
              <Button type="text" size="small" icon={<LeftOutlined className="text-blue-500" />} onClick={() => setCurrentValue(currentValue.subtract(1, 'month'))} />
              <span className="font-bold text-blue-600">{currentValue.format('MMMM YYYY')}</span>
              <Button type="text" size="small" icon={<RightOutlined className="text-blue-500" />} onClick={() => setCurrentValue(currentValue.add(1, 'month'))} />
            </div>
            <Calendar fullscreen={false} headerRender={() => null} value={currentValue} onSelect={setCurrentValue} />
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm flex-1 flex flex-col overflow-hidden border border-slate-100">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-700">Employees</span>
                {selectedEmployeeId && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => setSelectedEmployeeId(null)}
                    className="text-xs h-6 px-2"
                    title="Clear employee filter"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <Select
                value={benchFilter}
                style={{ width: 100 }}
                onChange={(value) => setBenchFilter(value)}
                variant="borderless"
                size="small"
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'vendor', label: 'Vendor Bench' },
                  { value: 'own', label: 'Own Bench' }
                ]}
              />
            </div>
            <Input
              prefix={<SearchOutlined className="text-slate-300" />}
              placeholder="search"
              className="mb-4 bg-slate-50 border-slate-100"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
            <InfiniteScrollList
              key={`${benchFilter}-${debouncedSearchQuery}-${currentValue.format('YYYY-MM')}`} // Reset component when filter, search, or date changes
              apiUrl="/calendar/interview-employees"
              apiQuery={{
                page: 1,
                limit: 20,
                search: debouncedSearchQuery,
                startDate: currentValue.startOf('month').format('YYYY-MM-DD'),
                endDate: currentValue.endOf('month').format('YYYY-MM-DD'),
                ...(benchFilter === 'vendor' ? { vendorPartner: 'Vendor' } : {}),
                ...(benchFilter === 'own' ? { vendorPartner: 'Own' } : {})
              }}
              height={400}
              itemsPerPage={20}
              reload={reloadEmployees}
              onLoaded={() => setReloadEmployees(false)}
              renderItem={(employee: any, index: number) => {
                if (!employee || !employee._id) return null;

                return (
                  <List.Item
                    key={employee._id || index}
                    className={`flex justify-between items-center hover:bg-slate-50 p-1 rounded-lg transition-colors cursor-pointer ${selectedEmployeeId === employee._id ? 'bg-blue-50' : ''
                      }`}
                    onClick={() => setSelectedEmployeeId(selectedEmployeeId === employee._id ? null : employee._id)}
                    style={{ padding: '8px 12px' }}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="bg-blue-100 text-blue-600 font-bold">
                        {employee.name?.charAt(0) || 'N'}
                      </Avatar>
                      <div>
                        <div className="text-sm font-semibold text-slate-700">{employee.name || 'N/A'}</div>
                        <div className="text-[10px] text-slate-400 uppercase font-medium">{employee.employeeId || 'N/A'}</div>
                      </div>
                    </div>
                    <span
                      className="bg-blue-50 text-blue-500 rounded-full px-2 py-0.5 text-xs font-bold cursor-pointer hover:bg-blue-100 transition-colors"
                      onClick={(e) => handleInterviewCountClick(e, employee)}
                      title="Click to view all interviews"
                    >
                      {employee.interviewCount || 0}
                    </span>
                  </List.Item>
                );
              }}
              listProps={{
                bordered: false,
              }}
            />
          </div>
        </aside>

        <main className="flex-1 bg-white rounded-xl shadow-sm flex flex-col overflow-hidden border border-slate-100">
          <div className="p-2 border-b border-slate-100 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Button onClick={() => setCurrentValue(dayjs())}>Today</Button>
              <div className="flex items-center gap-1 font-bold text-slate-700 text-base">
                {view === 'month' ? currentValue.format('MMMM YYYY') : `${currentValue.startOf(view).format('DD')} - ${currentValue.endOf(view).format('DD MMMM YYYY')}`}
                <RightOutlined className="text-[9px] ml-1 text-slate-400" />
              </div>
              <RangePicker style={{ width: 220 }} className="bg-slate-50 border-slate-100 ml-2" />
            </div>

            <div className="flex items-center gap-3">
              <Badge
                count={totalInterviewCount}
                size="small"
                color="#1677ff"
                style={{ cursor: 'pointer' }}
              >
                <Button
                  onClick={handleTotalInterviewCountClick}
                  shape="circle"
                  icon={<MessageOutlined />}
                  className="border-none bg-slate-50 hover:bg-slate-100"
                  title="Click to view all scheduled interviews"
                />
              </Badge>
              <Select
                value={view}
                style={{ width: 100 }}
                onChange={(val) => setView(val as any)}
                options={[
                  { value: 'day', label: 'Day' },
                  { value: 'week', label: 'Week' },
                  { value: 'month', label: 'Month' }
                ]}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="bg-blue-600"
                onClick={() => {
                  setSelectedDateForInterview(currentValue);
                  setIsAddInterviewModalVisible(true);
                }}
              >
                Create
              </Button>
              <Button type="text" icon={<MoreOutlined />} className="text-slate-400" />
            </div>
          </div>

          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Spin size="large" />
              </div>
            ) : view === 'month' ? (
              <Calendar
                headerRender={() => null}
                cellRender={dateCellRender}
                value={currentValue}
                onSelect={(date) => {
                  setCurrentValue(date);
                  // Cell selection logic handled in dateCellRender
                }}
                className="tailwind-calendar"
              />
            ) : (
              <TimeGridView />
            )}
          </div>
        </main>
      </div>

      {/* MODAL 1: DAILY TIMELINE LIST */}
      <ScheduleTimelineModal
        visible={isTimelineVisible}
        onClose={() => setIsTimelineVisible(false)}
        data={selectedTimelineData}
        titleRange={timelineTitle || (selectedTimelineData.length > 0 ? selectedTimelineData[0].date : currentValue.format('DD MMMM YYYY'))}
        onAddInterview={(dateStr) => {
          // Parse the date string (format: "DD MMMM YYYY") and set it for the interview modal
          // Try parsing with the format, if it fails, try without format
          let date = dayjs(dateStr, 'DD MMMM YYYY');
          if (!date.isValid()) {
            date = dayjs(dateStr);
          }
          const dateWithTime = date.isValid()
            ? date.hour(9).minute(0).second(0)
            : dayjs().hour(9).minute(0).second(0);
          setSelectedDateForInterview(dateWithTime);
          setIsTimelineVisible(false);
          setIsAddInterviewModalVisible(true);
        }}
      />

      {/* MODAL 2: SPECIFIC INTERVIEW DETAILS */}
      <InterviewScheduleModal
        visible={isDetailVisible}
        onClose={() => setIsDetailVisible(false)}
        event={selectedEvent}
        onEdit={handleEditInterview}
        onFeedbackAdded={handleFeedbackAdded}
      />

      <FeedbackDrawer
        visible={isDrawerOpen}
        handleCancel={handleClose}
        submitForm={handleSubmit}
        loading={false}
        isView={false} // Set to true if you want to hide the Footer buttons
        title="Recent Feedback"
      />

      <AddInterviewModal
        visible={isAddInterviewModalVisible}
        onClose={() => {
          setIsAddInterviewModalVisible(false);
          setIsEditMode(false);
          setEditInterviewData(null);
          setSelectedDateForInterview(undefined);
          setSelectedHour(null);
        }}
        onSuccess={() => {
          refreshInterviewData();
          setIsEditMode(false);
          setEditInterviewData(null);
          setSelectedDateForInterview(undefined);
          setSelectedHour(null);
        }}
        initialDate={selectedDateForInterview}
        editData={editInterviewData}
        isEditMode={isEditMode}
      />

      {/* Employee Interviews List Modal */}
      <Modal
        open={isEmployeeInterviewsVisible}
        onCancel={() => {
          setIsEmployeeInterviewsVisible(false);
          setSelectedEmployeeForInterviews(null);
          setEmployeeInterviews([]);
        }}
        title={
          selectedEmployeeForInterviews ? (
            <div className="flex items-center gap-3">
              <Avatar className="bg-blue-100 text-blue-600 font-bold">
                {selectedEmployeeForInterviews.name?.charAt(0) || 'N'}
              </Avatar>
              <div>
                <div className="font-bold text-lg">{selectedEmployeeForInterviews.name || 'Employee'}</div>
                <div className="text-xs text-gray-500">{selectedEmployeeForInterviews.employeeId || ''}</div>
              </div>
            </div>
          ) : 'Employee Interviews'
        }
        footer={null}
        width={700}
        centered
      >
        {loadingInterviews ? (
          <div className="flex justify-center items-center py-8">
            <Spin size="large" />
          </div>
        ) : employeeInterviews.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No interviews scheduled for this employee
          </div>
        ) : (
          <List
            dataSource={employeeInterviews}
            renderItem={(interview: any, index: number) => {
              const interviewDate = interview.date ? dayjs(interview.date) : null;
              const timeStr = interview.time || 'N/A';

              return (
                <List.Item
                  className="hover:bg-slate-50 rounded-lg px-4 py-3 cursor-pointer transition-colors"
                  onClick={() => {
                    // Open interview detail modal if interviewId is available
                    if (interview.interviewId) {
                      // You can implement opening the interview detail modal here
                      message.info('Click functionality can be added to view interview details');
                    }
                  }}
                >
                  <div className="flex justify-between items-center w-full">
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col items-center min-w-[80px]">
                        <span className="text-sm font-semibold text-gray-700">
                          {interviewDate ? interviewDate.format('DD MMM') : 'N/A'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {interviewDate ? interviewDate.format('YYYY') : ''}
                        </span>
                        <span className="text-xs font-medium text-blue-600 mt-1">
                          {timeStr}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800">
                          {interview.interviewerName || 'Interview Scheduled'}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {interview.job_description || 'No description'}
                        </div>
                      </div>
                    </div>
                    <Tag color="blue">Interview</Tag>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Modal>

      <style>{`
        .tailwind-calendar .ant-picker-cell-inner { 
          @apply !min-h-[120px] !p-2 !m-0 !rounded-none !block; 
          overflow: hidden !important;
        }
        .tailwind-calendar .ant-picker-calendar-date-content {
          overflow-y: hidden !important;
        }
        .tailwind-calendar .ant-picker-cell-inner > div {
          overflow: hidden;
        }
        .tailwind-calendar .ant-picker-cell-today .ant-picker-calendar-date-value {
          @apply bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center ml-auto font-bold text-xs;
        }
      `}</style>
    </ConfigProvider>
  );
};

export default CustomCalendar;